import { useEffect, useState } from 'react'
import { differenceInDays, fromUnixTime } from 'date-fns'
import { useAppSelector } from 'store/hooks'
import getPriceDetailsForCurrency from 'utils/getPriceDetailsForCurrency'
import sortBy from 'lodash/sortBy'
import getFormattedPrice from 'utils/getFormattedPrice'
import { LoadingState } from 'store/fetchingLogic'
import { useGetProductsQuery } from 'store/api/billing/products'
import { CommonPriceInterval, Payment } from 'store/api/billing/payments.interface'
import { useGetBillingPaymentsQuery } from 'store/api/billing'

export default function useCreditBalance(): { creditBalance: string; proRated: number } {
  const [creditBalance, setCreditBalance] = useState('')
  const [proRated, setProRate] = useState(0)

  const { priceInterval, selectedProductPk, currentProduct, selectedCurrency, loading } =
    useAppSelector(s => s.products)
  const { data: productsData } = useGetProductsQuery({})

  const selectedPricePoint = productsData?.products
    ?.find(p => p.PK === selectedProductPk)
    ?.price_points.find(pp => pp.duration === priceInterval)
  const selectedPrice =
    selectedPricePoint && getPriceDetailsForCurrency(selectedPricePoint, selectedCurrency?.code)
  const currentPrice =
    currentProduct?.price &&
    getPriceDetailsForCurrency(currentProduct?.price, selectedCurrency?.code)

  const { data: billingPaymentsData } = useGetBillingPaymentsQuery('')
  const [lastPayment] = sortBy(billingPaymentsData?.payments ?? [], [
    'ts',
  ]).reverse() as Array<Payment>

  const isLoading = loading === LoadingState.IDLE || loading === LoadingState.PENDING

  useEffect(() => {
    if (!currentProduct?.subscription?.status) {
      return setCreditBalance(getFormattedPrice(selectedCurrency, 0))
    }

    if (currentPrice && lastPayment && !isLoading) {
      const days = currentProduct?.price?.duration === CommonPriceInterval.MONTHLY ? 30 : 365
      const dailyPrice = currentPrice?.price / days

      const daysSinceLastPayment = differenceInDays(new Date(), fromUnixTime(lastPayment?.ts))

      /*
					the prorated price is amount of the current payment that is left. It calculated by subtracting the
					number of days since last payment multiplied by the daily price of the service from the price
					of the subscription
				*/
      const proRated = currentPrice.price - daysSinceLastPayment * dailyPrice
      setProRate(proRated)

      const creditBalance = -lastPayment?.balance + proRated - (selectedPrice?.price ?? 0)
      setCreditBalance(getFormattedPrice(selectedCurrency, Math.max(creditBalance, 0)))
    }
  }, [
    currentPrice,
    currentProduct?.price?.duration,
    currentProduct?.subscription?.status,
    isLoading,
    lastPayment,
    lastPayment?.balance,
    lastPayment?.ts,
    selectedCurrency,
    selectedPrice?.price,
  ])

  return {
    creditBalance,
    proRated,
  }
}
